export const SESSION_ERROR_EVENT = "collection:session-error";

export class ApiError extends Error {
  constructor(message, status, requestId) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.requestId = requestId;
  }
}

function csrfToken() {
  const cookie = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith("csrftoken="));
  return cookie ? decodeURIComponent(cookie.slice("csrftoken=".length)) : "";
}

// The profile GET creates the cookie. Read it again for every mutation because
// login can rotate it. The deployed helper does not serialize or validate JSON.
export function apiFetch(url, options = {}, fetcher) {
  const { skipSessionRefresh, ...requestOptions } = options;
  const hostHelper = !fetcher && window.csrfFetch;
  const headers = new Headers(options.headers);
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  if (
    !hostHelper &&
    !["GET", "HEAD", "OPTIONS"].includes(
      (options.method || "GET").toUpperCase(),
    )
  ) {
    const token = csrfToken();
    if (token) headers.set("X-CSRFToken", token);
  }
  const request = fetcher || (hostHelper ? hostHelper.bind(window) : fetch);
  return request(url, {
    ...requestOptions,
    credentials: "include",
    headers: Object.fromEntries(headers.entries()),
  });
}

export async function requestJson(url, options = {}, fetcher) {
  const response = await apiFetch(url, options, fetcher);
  let data = null;
  let parseError;
  try {
    if (
      ![204, 205].includes(response.status) &&
      options.method?.toUpperCase() !== "HEAD"
    ) {
      data = await response.json();
    }
  } catch (error) {
    if (error.name === "AbortError") throw error;
    parseError = error;
    // Error middleware may return HTML or an empty body. Never render that body.
  }
  const requestId =
    response.headers?.get("X-Request-ID") ||
    (typeof data?.code === "string" ? data.code : undefined);
  if (!response.ok) {
    const message =
      [data?.error, data?.detail, data?.message].find(
        (value) => typeof value === "string",
      ) || `Request failed with status ${response.status}`;
    const error = new ApiError(message, response.status, requestId);
    if ([401, 403].includes(response.status) && !options.skipSessionRefresh) {
      window.dispatchEvent(
        new CustomEvent(SESSION_ERROR_EVENT, { detail: error }),
      );
    }
    throw error;
  }
  if (parseError) {
    throw new ApiError(
      "Invalid JSON response. Refresh before retrying the operation.",
      response.status,
      requestId,
    );
  }
  return { data, status: response.status, requestId };
}

export function describeApiError(error) {
  const diagnostic = [error.status && `HTTP ${error.status}`, error.requestId]
    .filter(Boolean)
    .join(" · ");
  if (error.status === 403 && error.message !== "Permission denied") {
    return `Session or CSRF verification failed. Your work is preserved. Check the cookie/origin configuration and retry deliberately after the session refresh. ${diagnostic}`;
  }
  return `${error.message}${diagnostic ? ` (${diagnostic})` : ""}`;
}
